import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Teacher Report Modal for Virtual Classroom
 * Teacher fills out a lesson report after class ends
 */
const TeacherReportModal = ({
  isOpen,
  onClose,
  onSubmit,
  darkMode = false,
}) => {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState({
    presentationErrors: null,
    errorDetails: '',
    studentLiked: null,
    studentEngagement: null,
    technicalIssues: null,
    technicalDetails: '',
    notes: '',
  });

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit(reportData);
    // Reset form
    setReportData({
      presentationErrors: null,
      errorDetails: '',
      studentLiked: null,
      studentEngagement: null,
      technicalIssues: null,
      technicalDetails: '',
      notes: '',
    });
  };

  const isFormValid =
    reportData.presentationErrors !== null &&
    reportData.studentLiked !== null &&
    reportData.studentEngagement !== null &&
    reportData.technicalIssues !== null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white rounded-t-2xl">
          <button
            onClick={onClose}
            aria-label={t('classroom.report.close')}
            className="absolute top-4 right-4 p-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
          >
            <X size={20} />
          </button>
          <h2 className="text-2xl font-bold text-center text-gray-900">{t('classroom.report.title')}</h2>
          <p className="text-center text-gray-600 mt-1">{t('classroom.report.subtitle')}</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Presentation errors */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              {t('classroom.report.qErrors')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setReportData({...reportData, presentationErrors: false})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-medium ${
                  reportData.presentationErrors === false
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <Check size={20} className="inline mr-2" />
                {t('classroom.report.allGood')}
              </button>
              <button
                onClick={() => setReportData({...reportData, presentationErrors: true})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-medium ${
                  reportData.presentationErrors === true
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <X size={20} className="inline mr-2" />
                {t('classroom.report.yesErrors')}
              </button>
            </div>
            {reportData.presentationErrors === true && (
              <textarea
                value={reportData.errorDetails}
                onChange={(e) => setReportData({...reportData, errorDetails: e.target.value})}
                placeholder={t('classroom.report.errorsPlaceholder')}
                className="w-full mt-3 border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                rows="2"
              />
            )}
          </div>

          {/* Student liked */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              {t('classroom.report.qStudentLiked')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setReportData({...reportData, studentLiked: true})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-medium ${
                  reportData.studentLiked === true
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                😊 {t('classroom.report.likedYes')}
              </button>
              <button
                onClick={() => setReportData({...reportData, studentLiked: false})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-medium ${
                  reportData.studentLiked === false
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                😐 {t('classroom.report.likedNo')}
              </button>
            </div>
          </div>

          {/* Student engagement */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              {t('classroom.report.qEngagement')} <span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  onClick={() => setReportData({...reportData, studentEngagement: level})}
                  className={`w-12 h-12 rounded-lg border-2 transition font-bold text-lg ${
                    reportData.studentEngagement === level
                      ? 'border-brand-primary bg-brand-sage/20 text-brand-secondary'
                      : reportData.studentEngagement && reportData.studentEngagement >= level
                      ? 'border-brand-sage bg-brand-sage/20 text-brand-primary'
                      : 'border-gray-200 hover:border-gray-300 text-gray-400'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
              <span>{t('classroom.report.engagementLow')}</span>
              <span>{t('classroom.report.engagementHigh')}</span>
            </div>
          </div>

          {/* Technical issues */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              {t('classroom.report.qTechIssues')} <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setReportData({...reportData, technicalIssues: false})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-medium ${
                  reportData.technicalIssues === false
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                {t('classroom.report.no')}
              </button>
              <button
                onClick={() => setReportData({...reportData, technicalIssues: true})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition font-medium ${
                  reportData.technicalIssues === true
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                {t('classroom.report.yes')}
              </button>
            </div>
            {reportData.technicalIssues === true && (
              <textarea
                value={reportData.technicalDetails}
                onChange={(e) => setReportData({...reportData, technicalDetails: e.target.value})}
                placeholder={t('classroom.report.techPlaceholder')}
                className="w-full mt-3 border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                rows="2"
              />
            )}
          </div>

          {/* Additional notes */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              {t('classroom.report.notesLabel')}
            </label>
            <textarea
              value={reportData.notes}
              onChange={(e) => setReportData({...reportData, notes: e.target.value})}
              placeholder={t('classroom.report.notesPlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
              rows="3"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="p-6 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`w-full py-3 rounded-lg font-semibold transition ${
              !isFormValid
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
            }`}
          >
            {t('classroom.report.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherReportModal;
